import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, AlertCircle, ArrowLeft, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ImportResultsSummaryProps {
  results: {
    success: number;
    failed: number;
    skipped: number;
    errors: { name: string; error: string }[];
  };
  onBackToClients: () => void;
  onImportMore: () => void;
}

export function ImportResultsSummary({ 
  results, 
  onBackToClients, 
  onImportMore 
}: ImportResultsSummaryProps) {
  const { t } = useTranslation(['clients', 'common']);
  const hasErrors = results.failed > 0;

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4">
          {hasErrors ? (
            <div className="w-16 h-16 rounded-full bg-status-warning text-status-warning-foreground flex items-center justify-center">
              <AlertCircle className="h-8 w-8" />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-full bg-status-success text-status-success-foreground flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8" />
            </div>
          )}
        </div>
        <CardTitle>
          {hasErrors ? t('import.complete.titleWithErrors') : t('import.complete.title')}
        </CardTitle>
        <CardDescription>
          {t('import.complete.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <ResultStat 
            icon={<CheckCircle2 className="h-5 w-5" />}
            label={t('import.complete.success')}
            value={results.success}
            variant="success"
          />
          <ResultStat 
            icon={<XCircle className="h-5 w-5" />}
            label={t('import.complete.failed')}
            value={results.failed}
            variant="error"
          />
          <ResultStat 
            icon={<AlertCircle className="h-5 w-5" />}
            label={t('import.complete.skipped')}
            value={results.skipped}
            variant="warning"
          />
        </div>

        {/* Error details */}
        {results.errors.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-destructive">
              {t('import.complete.errorDetails')}
            </h4>
            <ScrollArea className="h-[150px] rounded-md border border-destructive/20 bg-destructive/5">
              <div className="p-3 space-y-2">
                {results.errors.map((error, index) => (
                  <div key={index} className="text-sm">
                    <span className="font-medium">{error.name}:</span>{' '}
                    <span className="text-muted-foreground">{error.error}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button variant="outline" onClick={onImportMore} className="flex-1">
            <RotateCcw className="h-4 w-4 mr-2" />
            {t('import.complete.importMore')}
          </Button>
          <Button onClick={onBackToClients} className="flex-1">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('import.complete.backToClients')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ResultStat({
  icon,
  label,
  value,
  variant,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  variant: 'success' | 'error' | 'warning';
}) {
  const bgColors = {
    success: 'bg-status-success text-status-success-foreground',
    error: 'bg-status-danger text-status-danger-foreground',
    warning: 'bg-status-warning text-status-warning-foreground',
  };

  return (
    <div className={`rounded-lg p-4 text-center ${bgColors[variant]}`}>
      <div className="flex justify-center mb-2">{icon}</div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-inherit">{label}</div>
    </div>
  );
}
